<template>
  <v-sheet class="d-flex align-center px-2" style="gap: 4px; height: 40px;">
    <!-- Play / Pause -->
    <v-btn icon small @click="togglePlay">
      <v-icon>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
    </v-btn>

    <!-- Current time -->
    <span class="caption text-no-wrap" style="min-width: 36px;">{{ formatTime(currentTime) }}</span>

    <!-- Progress slider -->
    <v-slider
      v-model="progress"
      :max="duration || 100"
      min="0"
      dense
      hide-details
      class="mx-1 flex-grow-1"
      style="min-width: 60px;"
      @mousedown="onProgressMousedown"
      @change="onProgressChange"
    />

    <!-- Duration -->
    <span class="caption text-no-wrap" style="min-width: 36px;">{{ formatTime(duration) }}</span>

    <!-- Mute button -->
    <v-btn icon small @click="toggleMute">
      <v-icon>{{ muted ? 'mdi-volume-off' : volume > 0.5 ? 'mdi-volume-high' : 'mdi-volume-low' }}</v-icon>
    </v-btn>

    <!-- Volume slider -->
    <v-slider
      v-model="volumePercent"
      min="0"
      max="100"
      dense
      hide-details
      style="max-width: 70px; min-width: 50px;"
      @change="onVolumeChange"
    />

    <!-- Download button -->
    <v-btn icon small :disabled="!blob" @click="downloadAudio">
      <v-icon>mdi-download</v-icon>
    </v-btn>
  </v-sheet>
</template>

<script>
export default {
  name: 'AudioPlayerExternal',

  props: {
    blob: {
      type: Blob,
      default: null,
    },
    fileName: {
      type: String,
      default: 'audio',
    },
    currentTime: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      audio: null,
      objectUrl: null,
      isPlaying: false,
      duration: 0,
      volume: 1,
      volumePercent: 100,
      muted: false,
      seeking: false,
      rafId: null,
    }
  },

  computed: {
    progress: {
      get() {
        return this.currentTime
      },
      set(val) {
        this.$emit('update:currentTime', val)
      },
    },
  },

  watch: {
    blob: {
      immediate: true,
      handler(blob) {
        this.destroyAudio()
        if (!blob) return
        this.objectUrl = URL.createObjectURL(blob)
        this.audio = new Audio(this.objectUrl)
        this.audio.volume = this.volume
        this.audio.muted = this.muted
        this.audio.addEventListener('loadedmetadata', () => {
          this.duration = this.audio.duration
        })
        this.audio.addEventListener('ended', () => {
          this.isPlaying = false
          this.$emit('update:currentTime', 0)
          cancelAnimationFrame(this.rafId)
        })
      },
    },
  },

  methods: {
    togglePlay() {
      if (!this.audio) return
      if (this.isPlaying) {
        this.audio.pause()
        this.isPlaying = false
        cancelAnimationFrame(this.rafId)
      } else {
        this.audio.play()
        this.isPlaying = true
        this.tick()
      }
    },

    tick() {
      if (!this.audio || !this.isPlaying) return
      if (!this.seeking) {
        this.$emit('update:currentTime', this.audio.currentTime)
      }
      this.rafId = requestAnimationFrame(this.tick)
    },

    onProgressMousedown() {
      this.seeking = true
    },

    onProgressChange(val) {
      if (!this.audio) return
      this.audio.currentTime = val
      this.$emit('update:currentTime', val)
      this.seeking = false
    },

    toggleMute() {
      if (!this.audio) return
      this.muted = !this.muted
      this.audio.muted = this.muted
    },

    onVolumeChange(val) {
      if (!this.audio) return
      this.volume = val / 100
      this.audio.volume = this.volume
      if (this.muted && val > 0) {
        this.muted = false
        this.audio.muted = false
      }
    },

    formatTime(sec) {
      if (!sec || isNaN(sec)) return '0:00'
      const m = Math.floor(sec / 60)
      const s = Math.floor(sec % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    downloadAudio() {
      if (!this.objectUrl) return
      const a = document.createElement('a')
      a.href = this.objectUrl
      a.download = this.fileName
      a.click()
    },

    destroyAudio() {
      if (this.audio) {
        this.audio.pause()
        this.audio.src = ''
        cancelAnimationFrame(this.rafId)
        this.audio = null
      }
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl)
        this.objectUrl = null
      }
      this.isPlaying = false
      this.$emit('update:currentTime', 0)
      this.duration = 0
    },
  },

  beforeDestroy() {
    this.destroyAudio()
  },
}
</script>
